import React, { useRef } from "react"

import { useUIState } from "../../../recoil/pluginStudio/hooks"
// Import LayoutState type from studioTypes but extend it for our component interface
import { LayoutState as BaseLayoutState } from "../types/studioTypes"
import CollapsiblePanel from "./CollapsiblePanel"
import ResizablePanel from "./ResizablePanel"
import VerticallyResizablePanel from "./VerticallyResizablePanel"

// Update the BaseLayoutState interface to include innerWidth
interface PanelDimensions {
  width?: string
  height?: string
  innerWidth?: string
}

export interface LayoutState {
  // Visibility state
  isFileExplorerVisible: boolean
  isLiveDataVisible: boolean
  isTestCasesVisible: boolean

  // Layout dimensions
  layouts: {
    fileExplorer?: PanelDimensions
    codePanel?: PanelDimensions
    previewPanel?: PanelDimensions
    testDataPanel?: PanelDimensions
    liveDataPanel?: PanelDimensions
  }

  // Actions
  setIsFileExplorerVisible: (visible: boolean) => void
  setIsLiveDataVisible: (visible: boolean) => void
  setIsTestCasesVisible: (visible: boolean) => void
  setLayouts: React.Dispatch<React.SetStateAction<BaseLayoutState>>
  handleToggleFileExplorer: () => void
}

/**
 * UI Component Props
 */
export interface StudioLayoutUIProps {
  fileExplorer: React.ReactNode
  codeEditor: React.ReactNode
  previewPanel: React.ReactNode
  testDataPanel: React.ReactNode
  liveDataPanel: React.ReactNode
  breadcrumbs: React.ReactNode
  fileExplorerVisible: boolean
  testPanelVisible: boolean
  liveDataVisible: boolean
  previewVisible: boolean
  toggleFileExplorerVisibility: () => void
  toggleTestPanelVisibility: () => void
  toggleLiveDataVisibility: () => void
  togglePreviewVisibility: () => void
}

/**
 * Presentation component that renders the studio layout UI
 */
export const StudioLayoutUI: React.FC<StudioLayoutUIProps> = ({
  fileExplorer,
  codeEditor,
  previewPanel,
  testDataPanel,
  liveDataPanel,
  breadcrumbs,
  fileExplorerVisible,
  testPanelVisible,
  liveDataVisible,
  previewVisible,
  toggleFileExplorerVisibility,
  toggleTestPanelVisibility,
  toggleLiveDataVisibility,
  togglePreviewVisibility,
}) => {
  // Create refs for measuring parent containers
  const mainContentRef = useRef<HTMLDivElement>(null)

  // Default dimensions
  const fileExplorerWidth = "240px"
  const codePanelWidth = "50%"
  const codePanelHeight = "55.743%"
  const previewPanelWidth = "50%"
  const testDataPanelHeight = "44.257%"
  const testDataInnerWidth = "50%"
  const liveDataInnerWidth = "50%"

  return (
    <div className="flex flex-col h-full bg-gray-900 text-gray-300">
      <div className="flex flex-1 overflow-hidden">
        {/* File Explorer (collapsible) */}
        <CollapsiblePanel
          isVisible={fileExplorerVisible}
          onToggle={toggleFileExplorerVisibility}
          title="File Explorer"
          width={fileExplorerWidth}
          position="left"
          resizable={false}
        >
          {fileExplorer}
        </CollapsiblePanel>

        {/* Main Content Area */}
        <div
          ref={mainContentRef}
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Breadcrumbs */}
          <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
            {breadcrumbs}
          </div>

          {/* Upper Section: Code + Preview */}
          <div
            className="flex flex-1 overflow-hidden relative"
            style={{ height: codePanelHeight }}
          >
            {/* Code Editor Panel */}
            <div
              className="flex-1 overflow-hidden"
              style={{ width: codePanelWidth }}
            >
              {codeEditor}
            </div>

            {/* Preview Panel */}
            {previewVisible && (
              <div
                className="overflow-hidden flex-1 bg-gray-900"
                style={{ width: previewPanelWidth }}
              >
                {previewPanel}
              </div>
            )}
          </div>

          {/* Lower Panels: Test Data + Live Data */}
          <div
            className="flex overflow-hidden"
            style={{ height: testDataPanelHeight }}
          >
            {/* Test Data Panel */}
            {testPanelVisible && (
              <div
                className="overflow-hidden bg-gray-800"
                style={{ width: testDataInnerWidth }}
              >
                {testDataPanel}
              </div>
            )}

            {/* Live Data Panel */}
            {liveDataVisible && (
              <div
                className="overflow-hidden bg-gray-800"
                style={{ width: liveDataInnerWidth }}
              >
                {liveDataPanel}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const StudioLayout: React.FC<{
  fileExplorer: React.ReactNode
  header: React.ReactNode
  codeEditor: React.ReactNode
  testDataPanel: React.ReactNode
  liveDataPanel: React.ReactNode
  previewPanel: React.ReactNode
  breadcrumbs: React.ReactNode
}> = ({
  fileExplorer,
  header,
  codeEditor,
  testDataPanel,
  liveDataPanel,
  previewPanel,
  breadcrumbs,
}) => {
  const { uiState, togglePanelVisibility } = useUIState()

  return (
    <div className="flex flex-col h-full bg-gray-900 text-gray-300">
      {/* Header */}
      {header}

      <StudioLayoutUI
        fileExplorer={fileExplorer}
        codeEditor={codeEditor}
        previewPanel={previewPanel}
        testDataPanel={testDataPanel}
        liveDataPanel={liveDataPanel}
        breadcrumbs={breadcrumbs}
        fileExplorerVisible={uiState.panels.fileExplorer.visible}
        testPanelVisible={uiState.panels.testPanel.visible}
        liveDataVisible={uiState.panels.liveDataPanel.visible}
        previewVisible={uiState.panels.previewPanel.visible}
        toggleFileExplorerVisibility={() =>
          togglePanelVisibility("fileExplorer")
        }
        toggleTestPanelVisibility={() => togglePanelVisibility("testPanel")}
        toggleLiveDataVisibility={() => togglePanelVisibility("liveDataPanel")}
        togglePreviewVisibility={() => togglePanelVisibility("previewPanel")}
      />
    </div>
  )
}

export default StudioLayout
